import { StrictMode, useEffect } from "react";
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";

import App from "./App.tsx";
import { i18nReady } from "./i18n.ts";
import {
  scrubSentryBreadcrumb,
  scrubSentryEvent,
} from "./utils/sanitizeSentryEvent.ts";

// Only initialize Sentry in specific environments
const enabledEnvironments = ["testing", "acceptance", "production"];
const currentEnvironment =
  import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";

if (enabledEnvironments.includes(currentEnvironment)) {
  // console.log(`Initializing Sentry in ${currentEnvironment} environment`);

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    // Never attach request bodies, cookies, headers or IP addresses by default;
    // we opt in to only the data we explicitly scrub below.
    sendDefaultPii: false,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
    ],
    tracesSampler: (samplingContext) => {
      // Filter out health check and other monitoring endpoints
      const url = samplingContext.location?.pathname;
      if (url && (url.includes("/health") || url.includes("/api/health"))) {
        return 0; // 0% sample rate for health checks
      }

      // Sample based on environment
      if (currentEnvironment === "production") {
        return 0.1; // 10% in production
      } else if (
        currentEnvironment === "acceptance" ||
        currentEnvironment === "testing"
      ) {
        return 0.5; // 50% in testing/acceptance
      }
      return 1.0; // 100% in development
    },
    beforeSend: (event, hint) => {
      // Filter out certain errors
      if (event.exception) {
        const error = hint.originalException as Error;

        // Filter out network errors for health checks
        if (error?.message?.includes("/health")) {
          return null;
        }

        // Filter out ResizeObserver errors (common browser issue)
        if (error?.message?.includes("ResizeObserver")) {
          return null;
        }

        // Filter out cancelled requests
        if (error && error.name === "AbortError") {
          return null;
        }
      }

      // Redact tokens/PII (query strings, auth headers, cookies) from the
      // request payload and breadcrumb URLs before the event leaves the client.
      return scrubSentryEvent(event);
    },
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // Set a reasonable max breadcrumbs
    maxBreadcrumbs: 50,
    // Don't capture console.log as breadcrumbs
    beforeBreadcrumb: (breadcrumb) => {
      if (breadcrumb.category === "console" && breadcrumb.level === "log") {
        return null;
      }
      // Redact any token-bearing query string from breadcrumb URLs (e.g. the
      // WebSocket `?id_token=` connect URL).
      return scrubSentryBreadcrumb(breadcrumb);
    },
    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      "top.GLOBALS",
      // Random browser errors
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // Network errors that are expected
      "NetworkError",
      "Network request failed",
    ],
  });

  Sentry.setTag("gitHash", import.meta.env.VITE_GIT_HASH);
  Sentry.setTag("gitCommitDate", import.meta.env.VITE_GIT_COMMIT_DATE);
  Sentry.setTag("appVersion", __APP_VERSION__);
} else {
  // console.log(
  //   `Sentry disabled in ${currentEnvironment || "unknown"} environment`,
  // );
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

i18nReady.then(() => {
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
});
